export type ReasoningGraphRecord = {
  id: string;
  kind: string;
  logicalId: string;
  revision: number;
  eligible: boolean;
  payload: Record<string, unknown>;
};

export type ReasoningGraphLink = {
  id: string;
  source: string;
  target: string;
  label: string;
  invalidation?: boolean;
};

/** The graph reads retained records; its only writable edges point into a new draft revision. */
export function reasoningGraphLinks(
  records: ReasoningGraphRecord[]
): ReasoningGraphLink[] {
  const known = new Set(records.map((row) => row.id));
  const links: ReasoningGraphLink[] = [];
  const link = (
    source: unknown,
    target: string,
    label: string,
    invalidation = false
  ) => {
    if (
      typeof source === "string" &&
      known.has(source) &&
      known.has(target) &&
      source !== target
    ) {
      links.push({
        id: `${source}:${target}:${label}`,
        source,
        target,
        label,
        invalidation,
      });
    }
  };
  for (const row of records) {
    const p = row.payload;
    if (row.kind === "candidate") {
      link(p.contextRecordId, row.id, "context");
      if (Array.isArray(p.derivedFromRecordIds)) {
        for (const parent of p.derivedFromRecordIds) {
          link(parent, row.id, "derived from");
        }
      }
    } else if (row.kind === "evaluation") {
      link(p.candidateRecordId, row.id, "evaluated");
    } else if (row.kind === "acceptance") {
      link(p.evaluationRecordId, row.id, "accepted");
      link(p.candidateRecordId, row.id, "candidate");
    } else if (row.kind === "step") {
      if (Array.isArray(p.inputRecordIds)) {
        for (const input of p.inputRecordIds) {
          link(input, row.id, "reported input");
        }
      }
    } else if (row.kind === "evidence-assessment") {
      link(p.candidateRecordId, row.id, "assessed");
    } else if (row.kind === "contribution") {
      link(p.baseCandidateRecordId, row.id, "contributed to");
    } else if (row.kind === "correction") {
      if (Array.isArray(p.affectedRecordIds)) {
        for (const affected of p.affectedRecordIds) {
          link(row.id, affected, "invalidates", true);
        }
      }
    }
  }
  return links;
}

export function candidateDraftParents(
  records: ReasoningGraphRecord[],
  selectedId: string | undefined,
  parentIds: string[]
): string[] {
  const candidates = new Set(
    records.filter((row) => row.kind === "candidate").map((row) => row.id)
  );
  // A revision always cites its predecessor, even if the author removes every optional parent.
  return [
    ...new Set([...(selectedId ? [selectedId] : []), ...parentIds]),
  ].filter((id) => candidates.has(id));
}
